/*
  LOBSTERVINE CUSTOMIZATION — Promo details popup
  Paired with sections/promo-details-popup.liquid

  Opens a popup when the visitor clicks any link whose path matches the section's
  "Trigger link URL" setting. Never auto-opens. Never reads or writes the theme's
  popup cookie, so it cannot suppress the built-in newsletter popup.

  Reuses the theme's own scroll-lock events and focus trap so behaviour matches
  <popup-large-promo>:
    - document event "theme:scroll:lock"   (detail: element to keep scrollable)
    - document event "theme:scroll:unlock"
    - window.theme.a11y.trapFocus / removeTrapFocus
*/
(function () {
  'use strict';

  var VISIBLE_CLASS = 'popup--visible';

  /* Normalise any href or setting value to a comparable path.
     Handles absolute URLs, relative URLs, trailing slashes and case. */
  function toPath(value) {
    if (!value) return '';
    try {
      return new URL(value, window.location.origin).pathname
        .replace(/\/+$/, '')
        .toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function anyPopupVisible() {
    return document.querySelector('.' + VISIBLE_CLASS) !== null;
  }

  class PromoDetailsPopup extends HTMLElement {
    connectedCallback() {
      this.inner = this.querySelector('[data-popup-inner]');
      this.popupBody = this.querySelector('[data-popup-body]');
      this.closeButton = this.querySelector('[data-popup-close]');
      this.underlay = this.querySelector('[data-popup-underlay]');

      if (!this.inner || !this.popupBody) return;

      this.triggerPath = toPath(this.dataset.triggerUrl);
      this.a11y = window.theme && window.theme.a11y ? window.theme.a11y : null;
      this.lastTrigger = null;

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeyup = this.handleKeyup.bind(this);
      this.onClose = this.close.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keyup', this.onKeyup);
      if (this.closeButton) this.closeButton.addEventListener('click', this.onClose);
      if (this.underlay) this.underlay.addEventListener('click', this.onClose);

      /* Theme editor: open when the section is selected in the sidebar,
         close when it is deselected. Mirrors the design-mode behaviour of
         the built-in Popups section. */
      if (window.Shopify && window.Shopify.designMode) {
        this.onSectionSelect = function (event) {
          if (event.target.contains(this)) this.open();
        }.bind(this);
        this.onSectionDeselect = function (event) {
          if (event.target.contains(this)) this.close();
        }.bind(this);

        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }
    }

    handleDocumentClick(event) {
      if (!this.triggerPath) return;

      var link = event.target.closest('a[href]');
      if (!link) return;

      /* Ignore links inside the popup itself. */
      if (this.contains(link)) return;

      /* Let modified clicks (new tab, download, etc.) behave normally —
         they fall through to the real page at the trigger URL. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      if (toPath(link.href) !== this.triggerPath) return;

      event.preventDefault();
      this.lastTrigger = link;
      this.open();
    }

    handleKeyup(event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      if (!this.inner.classList.contains(VISIBLE_CLASS)) return;
      this.close();
    }

    open() {
      if (this.inner.classList.contains(VISIBLE_CLASS)) return;

      if (window.theme && typeof window.theme.closeAllTooltips === 'function') {
        window.theme.closeAllTooltips();
      }

      this.inner.classList.add(VISIBLE_CLASS);

      if (this.a11y && typeof this.a11y.trapFocus === 'function') {
        this.a11y.trapFocus({container: this.popupBody});
      }

      document.dispatchEvent(
        new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.popupBody})
      );
    }

    close() {
      if (!this.inner.classList.contains(VISIBLE_CLASS)) return;

      this.inner.classList.remove(VISIBLE_CLASS);

      if (this.a11y && typeof this.a11y.removeTrapFocus === 'function') {
        this.a11y.removeTrapFocus();
      }

      /* Only release the page scroll if no other popup is still open —
         the theme's popups keep their own open-count that we can't see. */
      if (!anyPopupVisible()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }

      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }
      this.lastTrigger = null;
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onKeyup);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.onClose);
      if (this.underlay) this.underlay.removeEventListener('click', this.onClose);

      if (this.onSectionSelect) {
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      if (!anyPopupVisible()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }
    }
  }

  if (!customElements.get('promo-details-popup')) {
    customElements.define('promo-details-popup', PromoDetailsPopup);
  }
})();